/**
 * JukeboxControls — self-contained header widget for snapshot jukebox.
 *
 * Shows: JB toggle, bars selector, snapshot dropdown, save/cancel when dirty.
 * Vertical border lines on left/right create a boxed area within the header.
 * CSS-transition progress bar shows time until next snapshot.
 * Responds to transport pause/resume.
 */

import { useCallback, useRef, useEffect } from 'react';
import { useRuntime } from '../contexts/RuntimeProvider';
import { transportService } from '@/runtime/web';
import { getWebTransport } from '@/env/web/AudioContext';

export function JukeboxControls() {
  const {
    snapshots, activeSnapshotIndex, jukeboxEnabled, setJukeboxEnabled,
    snapshotBars, setSnapshotBars, isDirty,
    recallSnapshot, removeSnapshot,
    jukeboxSave, jukeboxCancel,
  } = useRuntime();

  const progressRef = useRef<HTMLDivElement>(null);
  // Track remaining duration so we can resume after pause
  const remainingRef = useRef(0);
  const startTimeRef = useRef(0);
  const totalDurationRef = useRef(0);

  const handleSave = useCallback(() => {
    const name = prompt('Snapshot name:');
    if (!name) return;
    jukeboxSave(name);
  }, [jukeboxSave]);

  const isRunning = jukeboxEnabled && !isDirty && snapshots.length > 1;

  // Start or restart progress animation
  const startProgress = useCallback((durationSec: number) => {
    const el = progressRef.current;
    if (!el) return;
    remainingRef.current = durationSec;
    totalDurationRef.current = durationSec;
    startTimeRef.current = performance.now() / 1000;

    el.style.transition = 'none';
    el.style.width = `${((1 - durationSec / totalDurationRef.current) * 100)}%`;
    requestAnimationFrame(() => {
      el.style.transition = `width ${durationSec}s linear`;
      el.style.width = '100%';
    });
  }, []);

  const pauseProgress = useCallback(() => {
    const el = progressRef.current;
    if (!el) return;
    // Freeze at current computed width
    const computed = getComputedStyle(el).width;
    el.style.transition = 'none';
    el.style.width = computed;
    // Calculate how much time remains
    const elapsed = performance.now() / 1000 - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, []);

  const resumeProgress = useCallback(() => {
    const el = progressRef.current;
    if (!el || remainingRef.current <= 0) return;
    startTimeRef.current = performance.now() / 1000;
    requestAnimationFrame(() => {
      el.style.transition = `width ${remainingRef.current}s linear`;
      el.style.width = '100%';
    });
  }, []);

  const stopProgress = useCallback(() => {
    const el = progressRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.width = '0%';
    remainingRef.current = 0;
  }, []);

  // Reset progress on snapshot advance or config change
  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;

    if (!isRunning) {
      stopProgress();
      return;
    }

    const { state, bpm } = transportService.getState();
    if (state !== 'started') {
      stopProgress();
      return;
    }

    const durationSec = snapshotBars * 4 * (60 / bpm);
    startProgress(durationSec);
  }, [isRunning, activeSnapshotIndex, snapshotBars, startProgress, stopProgress]);

  // Subscribe to transport start/stop/pause
  useEffect(() => {
    if (!isRunning) return;

    const transport = getWebTransport();

    const onStart = () => {
      const { bpm } = transportService.getState();
      const durationSec = snapshotBars * 4 * (60 / bpm);
      if (remainingRef.current > 0) {
        resumeProgress();
      } else {
        startProgress(durationSec);
      }
    };
    const onPause = () => pauseProgress();
    const onStop = () => stopProgress();

    transport.on('start', onStart);
    transport.on('pause', onPause);
    transport.on('stop', onStop);

    return () => {
      transport.off('start', onStart);
      transport.off('pause', onPause);
      transport.off('stop', onStop);
    };
  }, [isRunning, snapshotBars, startProgress, pauseProgress, resumeProgress, stopProgress]);

  return (
    <div style={styles.box}>
      {/* Progress bar background */}
      <div ref={progressRef} style={styles.progress} />

      {/* Content over progress bar */}
      <div style={styles.content}>
        {/* Jukebox on/off */}
        <button
          onClick={() => setJukeboxEnabled(!jukeboxEnabled)}
          style={{
            ...styles.toggle,
            background: jukeboxEnabled ? '#3b82f6' : '#374151',
            borderColor: jukeboxEnabled ? '#2563eb' : '#4b5563',
            color: jukeboxEnabled ? '#fff' : '#9ca3af',
          }}
          title={jukeboxEnabled ? 'Jukebox ON — click to disable' : 'Jukebox OFF — click to enable'}
        >
          JB
        </button>

        {/* Bars per snapshot */}
        <select
          value={snapshotBars}
          onChange={e => setSnapshotBars(Number(e.target.value))}
          style={styles.barsSelect}
          title="Bars per snapshot"
        >
          {[1, 2, 4, 8, 16, 32].map(n => (
            <option key={n} value={n}>{n}b</option>
          ))}
        </select>

        {/* Dirty state: save / cancel */}
        {isDirty && (
          <>
            <button onClick={handleSave} style={{ ...styles.action, background: '#3b82f6' }}>Save</button>
            <button onClick={jukeboxCancel} style={{ ...styles.action, background: '#6b7280' }}>Cancel</button>
          </>
        )}

        {/* Snapshot dropdown */}
        {snapshots.length > 0 && (
          <select
            value={activeSnapshotIndex >= 0 ? snapshots[activeSnapshotIndex]?.name ?? '' : ''}
            onChange={e => {
              if (e.target.value) recallSnapshot(e.target.value);
            }}
            style={styles.snapshotSelect}
            title="Recall snapshot (right-click option to remove)"
          >
            <option value="" disabled>Snapshots</option>
            {snapshots.map((s, idx) => (
              <option key={s.name} value={s.name}>
                {idx + 1}. {s.name}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  box: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderLeft: '1px solid #4b5563',
    borderRight: '1px solid #4b5563',
    overflow: 'hidden',
  },
  progress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '0%',
    background: 'rgba(59, 130, 246, 0.15)',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 8px',
    zIndex: 1,
  },
  toggle: {
    border: '1px solid',
    borderRadius: 4,
    padding: '4px 6px',
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
    lineHeight: 1,
  },
  barsSelect: {
    background: '#374151',
    color: '#e5e7eb',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '3px 4px',
    fontSize: 11,
    cursor: 'pointer',
  },
  action: {
    border: 'none',
    borderRadius: 4,
    color: 'white',
    fontSize: 10,
    padding: '4px 8px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  snapshotSelect: {
    background: '#374151',
    color: '#e5e7eb',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '3px 6px',
    fontSize: 11,
    cursor: 'pointer',
    maxWidth: 160,
  },
};
